import { Link, useNavigate, useLocation, useParams } from 'react-router-dom'
import { LogOut, ArrowLeft, Search, ArrowLeftRight } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useFirmaContext } from '@/contexts/FirmaContext'
import { assetUrl } from '@/utils/assetUrl'
import NotificationsBell from '@/components/NotificationsBell'

/**
 * Top-Navbar im FOM-Newsroom-Style.
 * Teal-Hintergrund, Logo links, MyFOM mittig, kontextabhängige Links rechts.
 */
export default function FirmaNavbar() {
  const { user, logout, isSuperAdmin, firmen } = useAuth()
  // useFirmaContext() is safe here: when rendered outside FirmaProvider the default
  // context returns canRead() = false for all sections (no-op fallback).
  const { canRead } = useFirmaContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { firmaToken } = useParams<{ firmaToken: string }>()

  const firmaHome = firmaToken
    ? `/firma/${firmaToken}`
    : firmen.length === 1
      ? `/firma/${firmen[0].token}`
      : '/'

  const isOnFirma = location.pathname.startsWith('/firma/')
  const isSubPage = location.pathname.includes('/teilnehmer/')
    || location.pathname.includes('/teilnehmer-anlegen')
    || location.pathname.includes('/einstellungen/')
    || location.pathname === '/admin'
    || location.pathname === '/anmeldeportal'
    || location.pathname === '/blackboard'
    || location.pathname === '/superadmin'

  const handleLogout = async () => {
    await logout()
    navigate('/portal/login')
  }

  const linkClass = 'text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/15 no-underline'
  const linkActiveClass = 'text-white text-sm font-semibold px-4 py-2 rounded-lg bg-white/15 no-underline'
  const homeLink = isSuperAdmin ? '/suche' : firmaHome

  return (
    <>
      {/* ── Top Navbar (FOM-Teal) ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-28 px-6 md:px-8 flex items-center bg-fom-500">

        {/* Links: FOM Negativ-Logo */}
        <Link to={homeLink} className="flex items-center no-underline shrink-0 mr-6">
          <img
            src={assetUrl('/fom-logo-negativ.png?v=20260527')}
            alt="FOM Hochschule"
            className="h-20 md:h-24 w-auto"
          />
        </Link>

        {/* Rechts: Nav-Links */}
        <div className="ml-auto flex items-center gap-1">
          {isSuperAdmin ? (
            <>
              {firmen.length > 1 && (
                <button
                  onClick={() => navigate('/select-firma', { state: { firmen } })}
                  className={`${linkClass} flex items-center gap-1.5 bg-transparent border-none cursor-pointer`}
                  title="Andere Firma auswählen"
                >
                  <ArrowLeftRight size={14} /> Firma wechseln
                </button>
              )}
              <Link to="/suche" className={location.pathname === '/suche' ? linkActiveClass : linkClass}>
                <span className="flex items-center gap-1.5"><Search size={14} />Firmensuche</span>
              </Link>
              {isOnFirma && (
                <>
                  <Link to={`${firmaHome}`} className={linkClass}>Startseite</Link>
                  <Link to={`${firmaHome}#teilnehmer`} className={linkClass}>Alle Studierenden</Link>
                </>
              )}
              <Link
                to="/blackboard"
                className={location.pathname === '/blackboard' ? linkActiveClass : linkClass}
              >
                Blackboard
              </Link>
              <Link
                to={isSuperAdmin ? '/superadmin' : '/admin'}
                className={(location.pathname === '/superadmin' || location.pathname === '/admin') ? linkActiveClass : linkClass}
              >
                Verwaltung
              </Link>
            </>
          ) : (
            <>
              {firmen.length > 1 && (
                <button
                  onClick={() => navigate('/select-firma', { state: { firmen } })}
                  className={`${linkClass} flex items-center gap-1.5 bg-transparent border-none cursor-pointer`}
                  title="Andere Firma auswählen"
                >
                  <ArrowLeftRight size={14} /> Firma wechseln
                </button>
              )}
              <Link to={firmaHome} className={linkClass}>Startseite</Link>
              {canRead('teilnehmer') && (
                <Link to={`${firmaHome}#teilnehmer`} className={linkClass}>Alle Studierenden</Link>
              )}
              {canRead('ansprechpartner') && (
                <Link to={`${firmaHome}#ansprechpartner`} className={linkClass}>Kontakt</Link>
              )}
            </>
          )}

          {user && (
            <>
              <div className="ml-2">
                <NotificationsBell />
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-white/70 text-sm font-medium px-3 py-1.5 rounded-lg transition-all duration-150 hover:bg-white/15 hover:text-white ml-1 border-none bg-transparent cursor-pointer"
                title="Abmelden"
              >
                <LogOut size={15} />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Platzhalter für fixed Navbar */}
      <div className="h-28" />

      {/* ── Zurück (echtes Browser-Back, sticky auf Unterseiten) ── */}
      {isSubPage && (
        <div className="sticky top-[114px] z-40 px-6 py-3 bg-[#F7F7F7] border-b border-gray-200">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-fom-600 text-sm font-medium hover:text-fom-700 bg-transparent border-none cursor-pointer px-0"
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
        </div>
      )}
    </>
  )
}
